import React, { useRef, useState, useEffect } from 'react';
import {
  Box, HStack, VStack, Text, IconButton, Button, Tooltip,
  Progress, useToast, useColorModeValue,
} from '@chakra-ui/react';
import { FiPaperclip, FiX, FiFile } from 'react-icons/fi';
import { firebase } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';

const MAX_TOTAL_SIZE = 25 * 1024 * 1024;

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AttachmentPicker({ attachments, onChange, uploadFileRef }) {
  const fileInputRef = useRef(null);
  const mountedRef = useRef(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { currentUser } = useAuth();
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const totalSize = attachments.reduce((sum, a) => sum + a.size, 0);

  const handleClick = () => fileInputRef.current?.click();

  const uploadFile = async (file) => {
    if (!file) return;
    const currentTotal = attachments.reduce((s, a) => s + a.size, 0);
    if (currentTotal + file.size > MAX_TOTAL_SIZE) {
      toast({ title: 'Attachments too large', description: `Total cannot exceed ${formatFileSize(MAX_TOTAL_SIZE)}.`, status: 'warning', duration: 3000 });
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const uid = currentUser?.uid || 'anonymous';
      const path = `email-attachments/${uid}/${Date.now()}_${file.name}`;
      const storageRef = firebase.storage().ref(path);
      const uploadTask = storageRef.put(file);

      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snap) => {
            if (mountedRef.current) setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
          },
          reject,
          resolve,
        );
      });

      const url = await storageRef.getDownloadURL();
      if (mountedRef.current) {
        onChange([...attachments, { name: file.name, size: file.size, url, mimeType: file.type }]);
      }
    } catch (err) {
      console.error('Attachment upload failed:', err);
      if (mountedRef.current) {
        toast({ title: 'Upload failed', description: err.message || 'Check console for details', status: 'error', duration: 4000 });
      }
    } finally {
      if (mountedRef.current) {
        setUploading(false);
        setProgress(0);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Expose uploadFile so the parent can trigger it from drag-and-drop
  useEffect(() => {
    if (uploadFileRef) uploadFileRef.current = uploadFile;
  });

  const handleFileChange = async (e) => {
    await uploadFile(e.target.files?.[0]);
  };

  const removeAttachment = (index) => {
    onChange(attachments.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <HStack spacing={2} mb={attachments.length > 0 ? 2 : 0}>
        <Button
          size="xs"
          variant="outline"
          leftIcon={<FiPaperclip />}
          onClick={handleClick}
          isLoading={uploading}
          loadingText="Uploading..."
        >
          Attach File
        </Button>
        {totalSize > 0 && (
          <Text fontSize="xs" color="gray.500">
            {formatFileSize(totalSize)} / {formatFileSize(MAX_TOTAL_SIZE)}
          </Text>
        )}
      </HStack>

      {uploading && <Progress size="xs" value={progress} colorScheme="blue" borderRadius="full" mb={2} />}

      {attachments.length > 0 && (
        <VStack align="stretch" spacing={1}>
          {attachments.map((att, i) => (
            <HStack
              key={i}
              px={2}
              py={1}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="md"
              fontSize="xs"
            >
              <FiFile />
              <Text flex={1} noOfLines={1}>{att.name}</Text>
              <Text color="gray.500">{formatFileSize(att.size)}</Text>
              <Tooltip label="Remove">
                <IconButton
                  icon={<FiX />}
                  aria-label="Remove attachment"
                  size="xs"
                  variant="ghost"
                  onClick={() => removeAttachment(i)}
                />
              </Tooltip>
            </HStack>
          ))}
        </VStack>
      )}
    </Box>
  );
}
